// src/components/ui/BedCard.tsx
// Promoted out of IPModule.tsx's local BedCard, which styled the whole IP
// bed board with inline hex — see the UI modernization assessment's 7th
// finding ("the IP bed board is styled entirely inline and would benefit
// from a BedCard primitive with occupancy encoded in form as well as
// colour"). Same three states, same tokens the rest of the design system
// already uses (Badge's success/warn/muted tones), plus a distinct leading
// glyph per state — ● occupied, ○ available, ⛭ maintenance — so status
// reads without relying on colour alone (a colour-blind viewer, or a
// low-quality print of the ward board, still tells the states apart).
import { Badge } from './Badge';
import { cn } from '@/lib/cn';

export type BedStatus = 'available' | 'occupied' | 'maintenance';

const STATUS_META: Record<BedStatus, {
  cardClass: string; badgeVariant: 'success' | 'warn' | 'neutral'; glyph: string; label: string;
}> = {
  available:   { cardClass: 'border-success/40 bg-success-soft/40',  badgeVariant: 'success', glyph: '○', label: 'Available' },
  occupied:    { cardClass: 'border-warn/40 bg-warn-soft/40',        badgeVariant: 'warn',     glyph: '●', label: 'Occupied' },
  maintenance: { cardClass: 'border-border bg-muted/60',             badgeVariant: 'neutral',  glyph: '⛭', label: 'Maintenance' },
};

export interface BedCardProps {
  bedNumber: string | number;
  status: BedStatus;
  patientName?: string;
  patientUhid?: string;
  /** Insurance Claims Phase 6 — an active PM-JAY/CMCHIS/hospital package on
   * this bed's admission. Only meaningful (and only ever true) when
   * `status === 'occupied'`; threaded from a single `EXISTS` LATERAL join
   * on the board's own one-shot query (routes/ip.js's GET /wards), not a
   * per-bed fetch — see that route's own comment for why this is safe to
   * show board-wide where the (costlier) claim-enhancement chip is not. */
  hasActivePackage?: boolean;
  /** The admission's live claim (same one-shot GET /ip/wards query): the
   * wireframe's "Cashless · SHT" chip plus the claim status, with a running
   * timer while an authorisation is out with the payer. */
  claim?: { mode: string | null; status: string; payerCode: string | null; authSentAt: string | null } | null;
  selected?: boolean;
  onClick?: () => void;
}

const CLAIM_MODE_LABEL: Record<string, string> = { cashless: 'Cashless', reimbursement: 'Reimbursement', scheme: 'Scheme', corporate: 'Corporate' };
const CLAIM_STATUS_LABEL: Record<string, string> = {
  draft: 'Claim draft', preauth_sent: 'Pre-auth sent', preauth_query: 'Pre-auth query', preauth_approved: 'Pre-auth approved',
  preauth_denied: 'Pre-auth denied', enhancement_sent: 'Enhancement sent', final_auth_sent: 'Final auth sent',
  final_auth_query: 'Final auth query', final_approved: 'Final approved',
};

function elapsed(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} m` : `${mins} m`;
}

export function BedCard({ bedNumber, status, patientName, patientUhid, hasActivePackage, claim, selected, onClick }: BedCardProps) {
  const meta = STATUS_META[status];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'min-w-[130px] rounded-[var(--radius)] border-2 px-3 py-2.5 text-left transition-colors',
        selected ? 'border-teal bg-teal/[.06]' : meta.cardClass,
        onClick ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[13px] font-bold">Bed {bedNumber}</span>
        <Badge variant={meta.badgeVariant}>
          <span aria-hidden className="mr-1">{meta.glyph}</span>{meta.label}
        </Badge>
      </div>
      {status === 'occupied' ? (
        <div className="text-xs text-foreground/80">
          {patientName} <span className="text-ink-muted">({patientUhid})</span>
          {(hasActivePackage || claim) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {claim && (
                <Badge variant="info" title="Payer for this admission">
                  {CLAIM_MODE_LABEL[claim.mode ?? ''] ?? 'Insured'}{claim.payerCode ? ` · ${claim.payerCode}` : ''}
                </Badge>
              )}
              {claim && CLAIM_STATUS_LABEL[claim.status] && (
                <Badge
                  variant={/query|denied/.test(claim.status) ? 'warn' : claim.status.endsWith('approved') ? 'success' : 'neutral'}
                  title="Claim status (Claims desk)"
                >
                  {CLAIM_STATUS_LABEL[claim.status]}{claim.authSentAt && /_sent$/.test(claim.status) ? ` · ${elapsed(claim.authSentAt)}` : ''}
                </Badge>
              )}
              {hasActivePackage && <Badge variant="info" title="A package is applied to this admission">📦 Package</Badge>}
            </div>
          )}
        </div>
      ) : (
        <div className="text-[11.5px] text-ink-muted">
          {status === 'available' ? 'Ready for admission' : 'Not in service'}
        </div>
      )}
    </button>
  );
}
